import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import useGetRequest from '../../api/useGetRequest';
import useOptimizeUnassignedSteps from '../../api/tours-planning/useOptimizeUnassignedSteps';
import useUnassignStep from '../../api/tours-planning/useUnassignStep';
import useAssignStep from '../../api/tours-planning/useAssignStep';
import useOptimizeVehicle from '../../api/tours-planning/useOptimizeVehicle';
import useRescheduleSteps from '../../api/tours-planning/useRescheduleSteps';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTruck,
  faRoute,
  faWeight,
  faEuroSign,
  faLeaf,
  faLayerGroup,
  faBoxesPacking,
  faMagic,
  faSpinner,
  faTrash,
  faPlus,
  faTimes,
  faFileExport,
  faCalendar,
  faExchangeAlt,
} from '@fortawesome/free-solid-svg-icons';
import L from 'leaflet';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  CircleMarker,
  Polyline,
  useMap,
} from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import moment from 'moment';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import fr from 'date-fns/locale/fr';
import { registerLocale } from 'react-datepicker';

import GenericPageContainer from '../../components/agv-generic-page-container';
import GenericTable from '../../components/agv-generic-table';
import GenericTableSkeleton from '../../components/agv-generic-table/genericTableSkeleton';
import EmptyList from '../../components/agv-empty-list';
import CustomDatePickerDashboard from '../../components/agv-header-date-picker';
import ManualAssignmentModal from './ManualAssignmentModal';
import RescheduleDateModal from './RescheduleDateModal';
import { decodeVroomGeometry } from '../../utils/polylineDecoder';
import './truck-assignments.css';

moment.locale('fr');
registerLocale('fr', fr);

// Composant pour afficher le véhicule sélectionné sur la carte
const VehicleNameOverlay = ({ vehicleName, vehicleColor, onClose }) => {
  const map = useMap();
  
  React.useEffect(() => {
    if (!vehicleName) return;
    
    const control = L.control({ position: 'topright' });
    
    control.onAdd = () => {
      const div = L.DomUtil.create('div', 'leaflet-bar vehicle-name-overlay');
      div.style.backgroundColor = 'white';
      div.style.padding = '12px 16px';
      div.style.borderRadius = '8px';
      div.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
      div.style.display = 'flex';
      div.style.alignItems = 'center';
      div.style.gap = '12px';
      div.style.cursor = 'pointer';
      div.style.transition = 'all 0.2s ease';
      div.style.border = `2px solid ${vehicleColor}`;
      
      div.innerHTML = `
        <div style="display: flex; align-items: center; gap: 12px;">
          <div style="
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: ${vehicleColor};
          "></div>
          <div>
            <div style="font-size: 11px; color: #666; margin-bottom: 2px;">Véhicule filtré</div>
            <div style="font-weight: 600; color: ${vehicleColor}; font-size: 14px;">
              ${vehicleName}
            </div>
          </div>
          <div style="
            margin-left: 8px;
            font-size: 18px;
            color: #666;
            font-weight: bold;
          ">×</div>
        </div>
      `;
      
      // Empêcher la propagation des événements de la carte
      L.DomEvent.disableClickPropagation(div);
      L.DomEvent.disableScrollPropagation(div);
      
      div.onclick = onClose;
      
      div.onmouseenter = () => {
        div.style.backgroundColor = '#f5f5f5';
        div.style.transform = 'scale(1.02)';
      };
      
      div.onmouseleave = () => {
        div.style.backgroundColor = 'white';
        div.style.transform = 'scale(1)';
      };
      
      return div;
    };
    
    control.addTo(map);
    
    return () => {
      control.remove();
    };
  }, [map, vehicleName, vehicleColor, onClose]);
  
  return null;
};

// Fonction pour éclaircir une couleur hexadécimale
const lightenColor = (hex, percent = 80) => {
  // Convertir hex en RGB
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);

  // Augmenter la luminosité en se rapprochant du blanc
  r = Math.round(r + (255 - r) * (percent / 100));
  g = Math.round(g + (255 - g) * (percent / 100));
  b = Math.round(b + (255 - b) * (percent / 100));

  // Reconvertir en hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Configuration pour les clusters de marqueurs
const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 'small';
  let className = 'marker-cluster-small';

  if (count >= 10) {
    size = 'medium';
    className = 'marker-cluster-medium';
  }
  if (count >= 100) {
    size = 'large';
    className = 'marker-cluster-large';
  }

  return L.divIcon({
    html: `
      <div style="
        background: linear-gradient(135deg, #079992 0%,rgb(10, 179, 170) 100%);
        border: 3px solid white;
        border-radius: 50%;
        width: ${size === 'small' ? '40px' : size === 'medium' ? '50px' : '60px'};
        height: ${size === 'small' ? '40px' : size === 'medium' ? '50px' : '60px'};
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: ${size === 'small' ? '14px' : size === 'medium' ? '16px' : '18px'};
        box-shadow: 0 4px 8px rgba(0,0,0,0.3);
        animation: pulse 2s infinite;
      ">
        ${count}
      </div>
    `,
    className: `marker-cluster ${className}`,
    iconSize: [
      size === 'small' ? 40 : size === 'medium' ? 50 : 60,
      size === 'small' ? 40 : size === 'medium' ? 50 : 60,
    ],
    iconAnchor: [
      size === 'small' ? 20 : size === 'medium' ? 25 : 30,
      size === 'small' ? 20 : size === 'medium' ? 25 : 30,
    ],
  });
};

export default function TruckAssignments() {
  // États pour les données
  const [deliveries, setDeliveries] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [deliveryCoordinates, setDeliveryCoordinates] = useState({});
  const [factoryCoordinates, setFactoryCoordinates] = useState([]);
  const [truckColors, setTruckColors] = useState({});
  const [showOptimizationBanner, setShowOptimizationBanner] = useState(false);
  const [vroomKpisByTour, setVroomKpisByTour] = useState({});

  const features = useSelector((state) => state.features);
  
  // Hook pour l'optimisation VROOM
  const {
    optimizeAllSteps,
    isOptimizing,
    optimizationResult,
    error: optimizationError,
    resetOptimization,
  } = useOptimizeUnassignedSteps();

  // Hook pour désaffecter une étape
  const {
    unassignStep,
    isUnassigning,
    error: unassignError,
  } = useUnassignStep();

  // Hook pour affecter manuellement une étape
  const {
    assignStep,
    isAssigning,
    error: assignError,
  } = useAssignStep();

  // Hook pour optimiser un véhicule spécifique
  const {
    optimizeVehicle,
    isOptimizing: isOptimizingVehicle,
    error: optimizeVehicleError,
  } = useOptimizeVehicle();

  // Hook pour replanifier des commandes
  const {
    rescheduleSteps,
    isRescheduling,
    error: rescheduleError,
  } = useRescheduleSteps();

  // État pour la modale d'affectation manuelle
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedDeliveryForAssignment, setSelectedDeliveryForAssignment] = useState(null);
  const [autoOptimizeAfterAssignment, setAutoOptimizeAfterAssignment] = useState(true);

  // État pour la modale de replanification
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  // État pour les itinéraires optimisés
  const [optimizedRoutes, setOptimizedRoutes] = useState([]);
  
  // État pour les jobs impossibles (dépassent les capacités)
  const [impossibleJobIds, setImpossibleJobIds] = useState([]);

  // État pour le véhicule sélectionné (filtrage)
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // KPI calculés
  const [kpiData, setKpiData] = useState({
    totalKilometers: 0,
    totalWeight: 0,
    totalTrucks: 0,
    totalDeliveries: 0,
    totalCost: 0,
    totalCO2: 0,
    totalMissions: 0,
    unassignedMissions: 0,
    missionsWithoutETA: 0,
    assignedMissions: 0,
    assignedWeight: 0,
    assignedDistance: 0,
    totalTravelTime: 0,
  });

  // Construction de l'URL pour l'API
  const buildApiUrl = useCallback(() => {
    const params = new URLSearchParams();
    params.append('tour_date', moment(selectedDate).format('YYYY-MM-DD'));
    params.append('status', 'SCHEDULED');
    return `/tours-planning/scheduled?${params.toString()}`;
  }, [selectedDate]);

  const scheduledToursUrl = buildApiUrl();

  // Hook pour récupérer les données planifiées
  const {
    data: scheduledData,
    isLoading: isLoadingScheduled,
    error: scheduledError,
    mutate: mutateScheduledData,
  } = useGetRequest(scheduledToursUrl, 300);

  // Référence à la carte
  const mapRef = React.useRef(null);

  // Effet pour gérer les erreurs
  useEffect(() => {
    if (scheduledError) {
      console.error('Erreur lors du chargement des données:', scheduledError);
      toast.error('Erreur lors du chargement des données planifiées');
    }
  }, [scheduledError]);

  // Effet pour gérer les erreurs d'optimisation
  useEffect(() => {
    if (optimizationError) {
      console.error('Erreur d\'optimisation:', optimizationError);
      toast.error(optimizationError);
    }
  }, [optimizationError]);

  // Effet pour gérer les erreurs de désaffectation
  useEffect(() => {
    if (unassignError) {
      console.error('Erreur de désaffectation:', unassignError);
      toast.error(unassignError);
    }
  }, [unassignError]);

  // Effet pour gérer les erreurs d'affectation
  useEffect(() => {
    if (assignError) {
      console.error('Erreur d\'affectation:', assignError);
      toast.error(assignError);
    }
  }, [assignError]);

  // Effet pour gérer les erreurs d'optimisation de véhicule
  useEffect(() => {
    if (optimizeVehicleError) {
      console.error('Erreur d\'optimisation du véhicule:', optimizeVehicleError);
      toast.error(optimizeVehicleError);
    }
  }, [optimizeVehicleError]);

  // Effet pour gérer les erreurs de replanification
  useEffect(() => {
    if (rescheduleError) {
      console.error('Erreur de replanification:', rescheduleError);
      toast.error(rescheduleError);
    }
  }, [rescheduleError]);

  // Réinitialiser les résultats d'optimisation quand la date change
  useEffect(() => {
    // Toujours réinitialiser les itinéraires, qu'ils viennent de l'optimisation ou de la BDD
    setOptimizedRoutes([]);
    setImpossibleJobIds([]);
    setSelectedVehicle(null);
    
    if (optimizationResult) {
      setShowOptimizationBanner(false);
      resetOptimization();
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effet pour traiter les données récupérées par l'API
  useEffect(() => {
    if (scheduledData) {
      processData(scheduledData);
    }
  }, [scheduledData]);

  const formatAddress = (street, zipcode, city) => {
    const parts = [street, zipcode, city].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Adresse non disponible';
  };

  const formatDate = (date) => {
    return date ? moment(date).format('DD/MM/YYYY') : null;
  };

  const processData = (res) => {
    // Extraire toutes les factories si elles sont présentes dans la réponse
    if (res && res.factories && res.factories.length > 0) {
      const factories = res.factories
        .filter(factory => factory && factory.latitude && factory.longitude)
        .map(factory => ({
          id: factory.id || factory.factory_id,
          name: factory.name || factory.factory_name || 'Usine',
          lat: parseFloat(factory.latitude),
          lng: parseFloat(factory.longitude),
        }));
      setFactoryCoordinates(factories);
    }

    // Utiliser les véhicules provenant de la réponse API
    let vehiclesArray = [];
    if (res && res.all_vehicles) {
      vehiclesArray = res.all_vehicles.map((vehicle) => ({
        id: vehicle.vehicule_id,
        key: vehicle.vehicule_key,
        licensePlate: vehicle.imat || 'Non spécifié',
        type: vehicle.vehicle_type || 'Non spécifié',
        capacity:
          typeof vehicle.total_capacity === 'number'
            ? vehicle.total_capacity
            : parseFloat(vehicle.total_capacity) || 1800,
        usedCapacity: 0,
        color: vehicle.color || '#cccccc',
      }));
    }

    // Extraire et décoder les géométries des tours depuis la BDD
    const routesFromDB = [];
    if (res && res.steps) {
      const toursWithGeometry = new Map();
      
      res.steps.forEach((step) => {
        const tourLinked = step.tour_linked_if_any;
        if (tourLinked && tourLinked.geometry && tourLinked.tour_id) {
          // Stocker une seule fois par tour_id
          if (!toursWithGeometry.has(tourLinked.tour_id)) {
            const vehicle = vehiclesArray.find(
              (v) => v.id === tourLinked.vehicle?.vehicule_id
            );
            
            try {
              const coordinates = decodeVroomGeometry(tourLinked.geometry);
              toursWithGeometry.set(tourLinked.tour_id, {
                tourId: tourLinked.tour_id,
                vehicleId: tourLinked.vehicle?.vehicule_id, // Ajouter vehicleId pour le matching
                vehicleKey: tourLinked.vehicule_key,
                color: vehicle?.color || '#079992',
                coordinates,
              });
            } catch (error) {
              console.error(
                `Erreur lors du décodage de la géométrie pour le tour ${tourLinked.tour_id}:`,
                error
              );
            }
          }
        }
      });

      routesFromDB.push(...Array.from(toursWithGeometry.values()));
      console.log(`${routesFromDB.length} itinéraire(s) chargé(s) depuis la BDD`);
    }

    // Fusionner les routes depuis la BDD avec celles déjà affichées
    // en priorisant les routes de la BDD (les plus à jour après optimisation)
    if (routesFromDB.length > 0) {
      setOptimizedRoutes((prevRoutes) => {
        // Créer un Map pour gérer les routes par vehicleId (clé la plus fiable)
        const routesMap = new Map();
        
        // D'abord, ajouter les routes existantes en mémoire
        prevRoutes.forEach(route => {
          const key = route.vehicleId || route.vehicleKey;
          if (key) {
            routesMap.set(String(key), route);
          }
        });
        
        // Puis, écraser avec les routes de la BDD (les plus récentes après sauvegarde)
        routesFromDB.forEach(route => {
          const key = route.vehicleId || route.vehicleKey;
          if (key) {
            routesMap.set(String(key), route);
          }
        });
        
        const mergedRoutes = Array.from(routesMap.values());
        console.log(`Routes fusionnées: ${mergedRoutes.length} itinéraire(s) au total`);
        return mergedRoutes;
      });
    }

    // Extraire les KPIs VROOM par tour
    const kpisByTour = {};
    if (res && res.steps) {
      res.steps.forEach((step) => {
        const tourLinked = step.tour_linked_if_any;
        if (tourLinked && tourLinked.tour_id) {
          // Stocker les KPIs une seule fois par tour
          if (!kpisByTour[tourLinked.tour_id]) {
            const totalDuration = tourLinked.vroom_total_duration || 0;
            const serviceTime = tourLinked.vroom_total_service_time || 0;
            const waitingTime = tourLinked.vroom_total_waiting_time || 0;
            const travelTime = totalDuration - serviceTime - waitingTime;

            kpisByTour[tourLinked.tour_id] = {
              totalDistance: tourLinked.vroom_total_distance,
              totalDuration: totalDuration,
              serviceTime: serviceTime,
              waitingTime: waitingTime,
              travelTime: travelTime > 0 ? travelTime : null,
              optimizedAt: tourLinked.vroom_optimized_at,
            };
          }
        }
      });
    }
    setVroomKpisByTour(kpisByTour);

    // Utiliser les étapes de livraison de la réponse API
    let deliveriesArray = [];
    if (res && res.steps) {
      const sortedSteps = res.steps.sort((a, b) => {
        const orderA = a.step_linked_if_any?.order || 0;
        const orderB = b.step_linked_if_any?.order || 0;
        return orderA - orderB;
      });

      deliveriesArray = sortedSteps.map((step, index) => {
        const endAddress = step.raw_data?.delivery_detail?.step?.end_address;
        const stepLinked = step.step_linked_if_any;
        const tourLinked = step.tour_linked_if_any;

        const clientName = endAddress?.name || 'N/A';
        const productCount = step.raw_data?.delivery_detail?.items?.length || 0;

        return {
          id: stepLinked?.step_id || `step_${index + 1}`,
          client: clientName,
          clientName: clientName,
          etaTime:
            step?.step_linked_if_any?.estimated_delivery &&
            moment(step.step_linked_if_any.estimated_delivery).format('HH:mm') !== '00:00'
              ? moment(step.step_linked_if_any.estimated_delivery).format('HH:mm')
              : null,
          street: endAddress?.street || 'N/A',
          zipcode: endAddress?.zipcode || 'N/A',
          city: endAddress?.city || 'N/A',
          address: formatAddress(
            endAddress?.street,
            endAddress?.zipcode,
            endAddress?.city
          ),
          products: `${productCount} produit(s)`,
          weight: parseFloat((step.total_weight || 0).toFixed(2)),
          date: formatDate(step.delivery_date) || 'N/A',
          truck: tourLinked?.vehicle?.imat || 'Non affecté',
          vehicleId: tourLinked?.vehicle?.vehicule_id || null,
          tourId: tourLinked?.tour_id || null,
          order: stepLinked?.order || 0,
          tourPlanningId: step.id,
          vroomKpis: tourLinked?.tour_id ? kpisByTour[tourLinked.tour_id] : null,
          coordinates:
            endAddress?.latitude !== undefined && endAddress?.longitude !== undefined
              ? {
                  lat: parseFloat(endAddress.latitude),
                  lng: parseFloat(endAddress.longitude),
                }
              : null,
        };
      });

      // Mettre à jour la capacité utilisée pour chaque camion
      vehiclesArray = vehiclesArray.map((truck) => {
        const assignedDeliveries = deliveriesArray.filter(
          (delivery) => delivery.truck === truck.licensePlate
        );

        const totalWeight = assignedDeliveries.reduce(
          (sum, delivery) => sum + (parseFloat(delivery.weight) || 0),
          0
        );

        return {
          ...truck,
          usedCapacity: parseFloat(totalWeight.toFixed(2)),
        };
      });
    }

    // Mettre à jour les états avec les données préparées
    setTrucks(vehiclesArray);
    setDeliveries(deliveriesArray);

    // Stocker les coordonnées des livraisons
    const coordinates = {};
    const colors = {};
    deliveriesArray.forEach((delivery) => {
      if (delivery.coordinates) {
        coordinates[delivery.id] = delivery.coordinates;
      }
      // Associer la couleur du camion
      const truck = vehiclesArray.find((t) => t.licensePlate === delivery.truck);
      if (truck && truck.color) {
        colors[delivery.id] = truck.color;
      }
    });
    setDeliveryCoordinates(coordinates);
    setTruckColors(colors);

    // Calculer les KPIs
    if (res && res.kpi) {
      calculateKPIsFromAPI(res.kpi, vehiclesArray, deliveriesArray);
    } else {
      calculateKPIs(vehiclesArray, deliveriesArray);
    }
  };

  const calculateKPIsFromAPI = (apiKpi, trucksData, deliveriesData) => {
    // Calculer les données manquantes côté front
    const costPerKm = 1.2;
    const co2PerKm = 0.8;

    console.log('apiKpi', apiKpi);

    const totalKm = apiKpi.totalDistance || 0;
    const totalCost = totalKm * costPerKm;
    const totalCO2 = totalKm * co2PerKm;

    // Calculer les missions non affectées et sans ETA côté front si non disponibles dans l'API
    const unassignedMissions = apiKpi.unassignedMissions !== undefined 
      ? apiKpi.unassignedMissions 
      : deliveriesData.filter(d => !d.vehicleId && (d.truck === 'Non affecté' || !d.truck)).length;
    
    const missionsWithoutETA = apiKpi.missionsWithoutETA !== undefined
      ? apiKpi.missionsWithoutETA
      : deliveriesData.filter(d => !d.etaTime).length;

    // Calculer les statistiques des missions affectées (tournées)
    const assignedDeliveries = deliveriesData.filter(d => d.vehicleId && d.truck !== 'Non affecté');
    const assignedMissions = assignedDeliveries.length;
    const assignedWeight = assignedDeliveries.reduce((sum, d) => sum + (parseFloat(d.weight) || 0), 0);

    // Calculer le temps de trajet total à partir de vroomKpisByTour
    const totalTravelTime = Object.values(vroomKpisByTour).reduce((sum, kpi) => {
      return sum + (kpi.travelTime || 0);
    }, 0);

    setKpiData({
      totalKilometers: parseFloat(totalKm.toFixed(2)),
      totalWeight: parseFloat((apiKpi.total_weight || 0).toFixed(2)),
      totalTrucks: apiKpi.totalTrucks || 0,
      totalDeliveries: apiKpi.totalDeliveries || deliveriesData.length,
      totalCost: parseFloat(totalCost.toFixed(2)),
      totalCO2: parseFloat(totalCO2.toFixed(2)),
      totalMissions: apiKpi.totalMissions || deliveriesData.length,
      unassignedMissions: unassignedMissions,
      missionsWithoutETA: missionsWithoutETA,
      assignedMissions: assignedMissions,
      assignedWeight: parseFloat(assignedWeight.toFixed(2)),
      assignedDistance: parseFloat(totalKm.toFixed(2)),
      totalTravelTime: totalTravelTime,
    });
  };

  const calculateKPIs = (trucksData, deliveriesData) => {
    // Calculer le poids total chargé
    const totalWeight = trucksData.reduce((sum, truck) => sum + truck.usedCapacity, 0);

    // Calculer le nombre de camions utilisés
    const trucksWithDeliveries = trucksData.filter((truck) =>
      deliveriesData.some((d) => d.truck === truck.licensePlate)
    ).length;

    // Estimations pour les autres KPIs (à ajuster selon vos données)
    const estimatedKmPerDelivery = 15;
    const totalKm = deliveriesData.length * estimatedKmPerDelivery;

    const costPerKm = 1.2;
    const totalCost = totalKm * costPerKm;

    const co2PerKm = 0.8;
    const totalCO2 = totalKm * co2PerKm;

    // Calculer les nouvelles métriques de missions
    const totalMissions = deliveriesData.length;
    const unassignedMissions = deliveriesData.filter(
      (d) => !d.vehicleId && (d.truck === 'Non affecté' || !d.truck)
    ).length;
    const missionsWithoutETA = deliveriesData.filter(
      (d) => !d.etaTime
    ).length;

    // Calculer les statistiques des missions affectées (tournées)
    const assignedDeliveries = deliveriesData.filter(d => d.vehicleId && d.truck !== 'Non affecté');
    const assignedMissions = assignedDeliveries.length;
    const assignedWeight = assignedDeliveries.reduce((sum, d) => sum + (parseFloat(d.weight) || 0), 0);
    const assignedDistance = assignedMissions * estimatedKmPerDelivery;

    // Calculer le temps de trajet total à partir de vroomKpisByTour
    const totalTravelTime = Object.values(vroomKpisByTour).reduce((sum, kpi) => {
      return sum + (kpi.travelTime || 0);
    }, 0);

    setKpiData({
      totalKilometers: parseFloat(totalKm.toFixed(2)),
      totalWeight: parseFloat(totalWeight.toFixed(2)),
      totalTrucks: trucksWithDeliveries,
      totalDeliveries: deliveriesData.length,
      totalCost: parseFloat(totalCost.toFixed(2)),
      totalCO2: parseFloat(totalCO2.toFixed(2)),
      totalMissions: totalMissions,
      unassignedMissions: unassignedMissions,
      missionsWithoutETA: missionsWithoutETA,
      assignedMissions: assignedMissions,
      assignedWeight: parseFloat(assignedWeight.toFixed(2)),
      assignedDistance: parseFloat(assignedDistance.toFixed(2)),
      totalTravelTime: totalTravelTime,
    });
  };

  // Fonction pour optimiser les tournées avec VROOM
  const handleOptimizeRoutes = async () => {
    try {
      const result = await optimizeAllSteps({
        tourDate: moment(selectedDate).format('YYYY-MM-DD'),
      });

      if (result.success) {
        // Traiter les routes avec géométrie
        console.log('Données VROOM reçues:', result.data);
        
        if (result.data?.routes) {
          console.log(`${result.data.routes.length} route(s) trouvée(s)`);
          
          const routesWithGeometry = result.data.routes
            .map((route) => {
              console.log('Route:', route.vehicleKey, 'Geometry:', route.geometry ? 'Présente' : 'Absente');
              
              // Trouver le camion correspondant
              const vehicle = trucks.find(t => t.id === route.vehicleId || t.key === route.vehicleKey);
              
              if (!route.geometry) {
                console.warn(`Pas de géométrie pour la route ${route.vehicleKey}`);
                return null;
              }

              try {
                // Décoder la géométrie de VROOM (format polyline)
                const coordinates = decodeVroomGeometry(route.geometry);
                console.log(`Route ${route.vehicleKey} décodée: ${coordinates.length} points`);
                
                return {
                  vehicleId: route.vehicleId,
                  vehicleKey: route.vehicleKey,
                  color: vehicle?.color || '#079992',
                  coordinates,
                  totalDistance: route.totalDistance,
                  totalDuration: route.totalDuration,
                };
              } catch (error) {
                console.error(`Erreur lors du décodage de la géométrie pour ${route.vehicleKey}:`, error);
                return null;
              }
            })
            .filter(Boolean);

          console.log(`${routesWithGeometry.length} itinéraire(s) prêt(s) pour affichage`);
          setOptimizedRoutes(routesWithGeometry);
        } else {
          console.warn('Pas de routes dans result.data');
        }

        // Afficher la bannière avec les résultats
        setShowOptimizationBanner(true);
        
        // Afficher un message de succès avec les détails
        const summary = result.data?.summary;
        let message = 'Optimisation réussie ! ';
        
        if (summary) {
          message += `${summary.totalRoutes} tournée(s) générée(s), `;
          if (summary.totalDistance) {
            message += `${(summary.totalDistance / 1000).toFixed(2)} km, `;
          }
          message += `${summary.totalDuration} minutes.`;
          
          if (result.data?.unassignedJobs?.length > 0) {
            message += ` ${result.data.unassignedJobs.length} commande(s) non affectée(s).`;
          }
        }
        
        toast.success(message, { autoClose: 5000 });
        
        // Stocker les IDs des jobs impossibles pour les afficher en rouge
        if (result.data?.impossibleJobs && result.data.impossibleJobs.length > 0) {
          const impossibleIds = result.data.impossibleJobs.map(j => j.tourPlanningId);
          setImpossibleJobIds(impossibleIds);
          
          const impossibleDetails = result.data.impossibleJobs
            .map(j => `• ${j.clientName}: ${j.details}`)
            .join('\n');
          
          toast.error(
            `❌ ${result.data.impossibleJobs.length} commande(s) impossible(s) à assigner :\n${impossibleDetails}`,
            { autoClose: 15000 }
          );
        }
        
        // Afficher un avertissement pour les véhicules ignorés
        if (result.data?.ignoredVehicles && result.data.ignoredVehicles.length > 0) {
          const ignoredDetails = result.data.ignoredVehicles
            .map(v => `• ${v.licensePlate} (${v.vehicleKey}): ${v.reason === 'missing_coordinates' ? 'Coordonnées manquantes' : v.reason}`)
            .join('\n');
          
          toast.warning(
            `⚠️ ${result.data.ignoredVehicles.length} véhicule(s) non utilisé(s) :\n${ignoredDetails}`,
            { autoClose: 10000 }
          );
        }
        
        // Rafraîchir les données pour afficher les nouvelles affectations
        mutateScheduledData();
        
        // Masquer la bannière après 15 secondes
        setTimeout(() => {
          setShowOptimizationBanner(false);
        }, 15000);
      }
    } catch (error) {
      console.error('Erreur lors de l\'optimisation:', error);
      toast.error(error.message || 'Erreur lors de l\'optimisation des tournées');
    }
  };

  // Fonction pour désaffecter une commande d'un camion
  const handleUnassignDelivery = async (delivery) => {
    // Vérifier si la commande est bien affectée
    if (!delivery.tourId || !delivery.id) {
      toast.error('Impossible de désaffecter cette commande');
      return;
    }

    // Demander confirmation
    const confirmMessage = `Êtes-vous sûr de vouloir désaffecter la commande pour ${delivery.client} du camion ${delivery.truck} ?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const result = await unassignStep({
        tourId: delivery.tourId,
        stepId: delivery.id,
      });

      if (result.success) {
        toast.success(`Commande désaffectée avec succès`);
        
        // Rafraîchir les données
        mutateScheduledData();
      }
    } catch (error) {
      console.error('Erreur lors de la désaffectation:', error);
      // L'erreur est déjà gérée par l'effet useEffect
    }
  };

  // Ouvrir la modale d'affectation manuelle pour une commande
  const handleOpenAssignmentModal = (delivery) => {
    setSelectedDeliveryForAssignment(delivery);
    setShowAssignmentModal(true);
  };

  // Fermer la modale d'affectation manuelle
  const handleCloseAssignmentModal = () => {
    if (!isAssigning) {
      setShowAssignmentModal(false);
      setSelectedDeliveryForAssignment(null);
    }
  };

  // Affecter manuellement une commande à un camion
  const handleManualAssignment = async (truck) => {
    if (!selectedDeliveryForAssignment || !truck) {
      toast.error('Impossible d\'affecter cette commande');
      return;
    }

    // Vérifier si c'est une réaffectation (commande déjà affectée à un autre camion)
    const isReassignment = selectedDeliveryForAssignment.vehicleId && selectedDeliveryForAssignment.tourId;
    const previousTruck = selectedDeliveryForAssignment.truck;

    try {
      const result = await assignStep({
        tourPlanningIds: [selectedDeliveryForAssignment.tourPlanningId],
        vehicleKey: truck.key,
        tourDate: moment(selectedDate).format('YYYY-MM-DD'),
        optimize: autoOptimizeAfterAssignment, // Envoyer l'option d'optimisation au backend
      });

      if (result.success) {
        // Vérifier si l'optimisation a été effectuée
        if (result.data?.optimization) {
          const optimization = result.data.optimization;
          const distanceReduction = optimization.improvements?.totalDistanceReduction || 0;
          
          if (isReassignment) {
            toast.success(
              `Commande réaffectée de ${previousTruck} vers ${truck.licensePlate} et tournée optimisée ! ${distanceReduction.toFixed(1)}km économisés`,
              { autoClose: 5000 }
            );
          } else {
            toast.success(
              `Commande affectée et tournée optimisée avec succès ! ${distanceReduction.toFixed(1)}km économisés`,
              { autoClose: 5000 }
            );
          }
        } else {
          if (isReassignment) {
            toast.success(`Commande réaffectée de ${previousTruck} vers ${truck.licensePlate} avec succès`);
          } else {
            toast.success(`Commande affectée au camion ${truck.licensePlate} avec succès`);
          }
        }
        
        // Fermer la modale
        handleCloseAssignmentModal();
        
        // Rafraîchir les données
        mutateScheduledData();
      }
    } catch (error) {
      console.error('Erreur lors de l\'affectation:', error);
      // L'erreur est déjà gérée par l'effet useEffect
    }
  };

  // Optimiser un véhicule spécifique
  const handleOptimizeVehicle = async (truckInfo) => {
    if (!truckInfo || !truckInfo.id) {
      toast.error('Impossible d\'optimiser ce véhicule');
      return;
    }

    try {
      const result = await optimizeVehicle({
        vehicleId: truckInfo.id,
        tourDate: moment(selectedDate).format('YYYY-MM-DD'),
      });

      if (result.success) {
        console.log('Résultat optimisation véhicule:', result.data);
        
        // Mettre à jour la géométrie de la route sur la carte
        if (result.data?.route?.geometry) {
          try {
            const coordinates = decodeVroomGeometry(result.data.route.geometry);
            
            // Mettre à jour optimizedRoutes pour ce véhicule
            setOptimizedRoutes((prevRoutes) => {
              // Supprimer l'ancienne route de ce véhicule
              const filteredRoutes = prevRoutes.filter(
                (r) => r.vehicleId !== truckInfo.id && r.vehicleKey !== truckInfo.key
              );
              
              // Ajouter la nouvelle route optimisée
              return [
                ...filteredRoutes,
                {
                  vehicleId: truckInfo.id,
                  vehicleKey: truckInfo.key,
                  color: truckInfo.color || '#079992',
                  coordinates,
                  totalDistance: result.data.route.totalDistance,
                  totalDuration: result.data.route.totalDuration,
                },
              ];
            });

            console.log('Géométrie mise à jour pour le véhicule', truckInfo.licensePlate);
          } catch (error) {
            console.error('Erreur lors du décodage de la géométrie:', error);
          }
        }

        // Afficher un message de succès
        const route = result.data?.route;
        let message = `Véhicule ${truckInfo.licensePlate} optimisé avec succès ! `;
        
        if (route) {
          if (route.totalDistance) {
            message += `Distance: ${(route.totalDistance / 1000).toFixed(2)} km, `;
          }
          if (route.totalDuration) {
            const hours = Math.floor(route.totalDuration / 60);
            const minutes = route.totalDuration % 60;
            message += `Durée: ${hours > 0 ? `${hours}h${minutes}min` : `${minutes}min`}`;
          }
        }
        
        toast.success(message, { autoClose: 5000 });
        
        // Rafraîchir les données pour afficher les nouveaux ordres et ETA
        // Ajouter un petit délai pour être sûr que la transaction SQL est commitée
        setTimeout(() => {
          mutateScheduledData();
        }, 200);
      }
    } catch (error) {
      console.error('Erreur lors de l\'optimisation du véhicule:', error);
      // L'erreur est déjà gérée par l'effet useEffect
    }
  };

  // Désaffecter toutes les commandes d'un camion
  const handleUnassignAllFromTruck = async (truckName, truckDeliveries) => {
    if (!truckDeliveries || truckDeliveries.length === 0) {
      return;
    }

    // Demander confirmation
    const confirmMessage = `Êtes-vous sûr de vouloir désaffecter toutes les ${truckDeliveries.length} commande(s) du camion ${truckName} ?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    // Désaffecter toutes les commandes une par une
    for (const delivery of truckDeliveries) {
      if (delivery.tourId && delivery.id) {
        try {
          const result = await unassignStep({
            tourId: delivery.tourId,
            stepId: delivery.id,
          });

          if (result.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error(`Erreur lors de la désaffectation de la commande ${delivery.id}:`, error);
          errorCount++;
        }
      }
    }

    // Afficher un message de résultat
    if (errorCount === 0) {
      toast.success(`${successCount} commande(s) désaffectée(s) avec succès du camion ${truckName}`);
    } else if (successCount > 0) {
      toast.warning(`${successCount} commande(s) désaffectée(s), ${errorCount} erreur(s)`);
    } else {
      toast.error(`Impossible de désaffecter les commandes du camion ${truckName}`);
    }

    // Rafraîchir les données
    mutateScheduledData();
  };

  // Replanifier les commandes non affectées vers une autre date
  const handleRescheduleUnassigned = async (newDate) => {
    // Récupérer les commandes non affectées
    const unassignedDeliveries = deliveries.filter(
      (delivery) => !delivery.vehicleId && (delivery.truck === 'Non affecté' || !delivery.truck)
    );

    if (unassignedDeliveries.length === 0) {
      toast.warning('Aucune commande non affectée à replanifier');
      return;
    }

    try {
      // Extraire les tour_planning_ids
      const tourPlanningIds = unassignedDeliveries
        .map((delivery) => delivery.tourPlanningId)
        .filter((id) => id !== null && id !== undefined);

      if (tourPlanningIds.length === 0) {
        toast.error('Impossible de replanifier : aucun ID de commande valide');
        return;
      }

      const result = await rescheduleSteps({
        tourPlanningIds,
        newTourDate: moment(newDate).format('YYYY-MM-DD'),
      });

      if (result.success) {
        toast.success(
          `${result.data.rescheduledCount} commande(s) replanifiée(s) pour le ${moment(newDate).format('DD/MM/YYYY')}`
        );
        
        // Fermer la modale
        setShowRescheduleModal(false);
        
        // Rafraîchir les données
        mutateScheduledData();
      }
    } catch (error) {
      console.error('Erreur lors de la replanification:', error);
      // L'erreur est déjà gérée par l'effet useEffect
    }
  };

  // Exporter les affectations en CSV
  const handleExportAssignments = () => {
    if (!scheduledData || !scheduledData.steps || scheduledData.steps.length === 0) {
      toast.warning('Aucune donnée à exporter');
      return;
    }

    try {
      // Préparer les données pour l'export
      const exportData = [];
      
      // Date d'expédition = date de planification (selectedDate)
      const expeditionDate = moment(selectedDate).format('DD/MM/YYYY');
      
      scheduledData.steps.forEach((step) => {
        const tourLinked = step.tour_linked_if_any;
        const deliveryDetail = step.raw_data?.delivery_detail;
        
        // Extraire le numéro de commande (comme affiché dans le puits de commandes)
        const orderNumber = deliveryDetail?.delivery_detail_key || 
                          deliveryDetail?.order_number || 
                          deliveryDetail?.external_id || 
                          deliveryDetail?.reference || 
                          'N/A';
        
        // Extraire le camion
        const truckName = tourLinked?.vehicle?.imat || 'Non affecté';
        
        // Extraire le client
        const clientName = step.raw_data?.delivery_detail?.step?.end_address?.name || 'N/A';
        
        // Si le delivery detail contient des items (lignes de commande), créer une ligne par item
        if (deliveryDetail?.items && deliveryDetail.items.length > 0) {
          deliveryDetail.items.forEach((item, index) => {
            exportData.push({
              NUM_COMMANDE: orderNumber,
              CAMION: truckName,
              DATE_D_EXPEDITION: expeditionDate,
              NUM_DE_LIGNE_DE_CMD: item.line_number || item.item_number || (index + 1),
              CLIENT: clientName,
              POIDS_KG: item.weight || 'N/A',
            });
          });
        } else {
          // Si pas d'items, créer une ligne pour la commande
          exportData.push({
            NUM_COMMANDE: orderNumber,
            CAMION: truckName,
            DATE_D_EXPEDITION: expeditionDate,
            NUM_DE_LIGNE_DE_CMD: '1',
            CLIENT: clientName,
            POIDS_KG: step.total_weight || 'N/A',
          });
        }
      });

      if (exportData.length === 0) {
        toast.warning('Aucune donnée à exporter');
        return;
      }

      // Créer le CSV
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(';'),
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header];
            // Échapper les valeurs contenant des virgules ou des guillemets
            if (typeof value === 'string' && (value.includes(';') || value.includes('"') || value.includes('\n'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(';')
        )
      ].join('\n');

      // Créer un blob et télécharger le fichier
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const fileName = `affectations_camions_${moment(selectedDate).format('YYYY-MM-DD')}.csv`;
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Export réussi : ${exportData.length} ligne(s) exportée(s)`);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      toast.error('Erreur lors de l\'export des données');
    }
  };

  // Grouper les commandes par camion
  const groupDeliveriesByTruck = () => {
    const grouped = {};
    
    deliveries.forEach((delivery) => {
      const truckKey = delivery.truck || 'Non affecté';
      if (!grouped[truckKey]) {
        grouped[truckKey] = [];
      }
      grouped[truckKey].push(delivery);
    });

    return grouped;
  };

  // Préparer les données pour une GenericTable par camion
  const prepareTableRowsForTruck = (truckDeliveries, isUnassigned = false) => {
    console.log('truckDeliveries', truckDeliveries);
    return truckDeliveries.map((delivery) => {
      // Vérifier si ce delivery est un job impossible
      const isImpossible = impossibleJobIds.includes(delivery.tourPlanningId);
      
      const rowData = {
        title: delivery.client,
        subtitle: `${delivery.address} - ${delivery.date}`,
        fields: [
          { name: 'Poids', value: `${delivery.weight} kg` },
          { name: 'Produits', value: delivery.products },
          { name: 'ETA', value: delivery.etaTime || 'Non défini' },
          { name: 'Ordre', value: delivery.order || '-' },
        ],
        icon: faTruck,
        // Appliquer un style rouge si c'est un job impossible
        style: isImpossible ? {
          backgroundColor: '#ffebee',
          border: '2px solid #d32f2f',
          opacity: 0.9,
        } : undefined,
        // Ajouter une indication visuelle
        badge: isImpossible ? {
          text: '⚠️ IMPOSSIBLE',
          color: '#d32f2f',
        } : undefined,
      };

      // Ajouter les actions selon l'état d'affectation
      rowData.actions = [];

      if (isUnassigned) {
        // Bouton d'affectation manuelle pour les commandes non affectées
        rowData.actions.push({
          icon: faPlus,
          onClick: () => handleOpenAssignmentModal(delivery),
          tooltip: 'Affecter manuellement à un camion',
          color: '#fff',
          disabled: isAssigning,
          className: "tw-flex tw-items-center tw-justify-center tw-p-2 tw-text-sm tw-font-medium tw-bg-primary tw-border-none tw-rounded-md disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
        });
      } else if (delivery.tourId && delivery.id) {
        // Boutons pour les commandes affectées
        // 1. Bouton de réaffectation
        rowData.actions.push({
          icon: faExchangeAlt,
          onClick: () => handleOpenAssignmentModal(delivery),
          tooltip: 'Réaffecter à un autre camion',
          color: '#fff',
          disabled: isAssigning,
          className: "tw-flex tw-items-center tw-justify-center tw-p-2  tw-text-sm tw-font-medium tw-bg-primary tw-border-none tw-rounded-md disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"

        });
        // 2. Bouton de désaffectation
        rowData.actions.push({
          icon: faTimes,
          onClick: () => handleUnassignDelivery(delivery),
          tooltip: 'Désaffecter cette commande',
          color: '#dc3545',
          disabled: isUnassigning,
          className: 'tw-flex tw-items-center  tw-p-2  tw-text-sm tw-font-medium tw-text-red-600 tw-bg-red-50 tw-border-none tw-rounded-md hover:tw-bg-red-100 hover:tw-border-red-300 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors',
        });
      }

      console.log('rowData', rowData);

      return rowData;
    });
  };

  // Gérer la sélection/déselection d'un véhicule
  const handleVehicleClick = (truckName) => {
    if (truckName === 'Non affecté') return; // Ne pas permettre la sélection des commandes non affectées
    
    setSelectedVehicle((prev) => {
      if (prev === truckName) {
        return null; // Déselectionner si on clique sur le même véhicule
      }
      return truckName;
    });
  };

  // Obtenir les groupes de commandes organisés
  const getOrderedTruckGroups = () => {
    const grouped = groupDeliveriesByTruck();
    const orderedGroups = [];

    // D'abord les commandes non affectées
    if (grouped['Non affecté']) {
      orderedGroups.push({
        truckName: 'Non affecté',
        deliveries: grouped['Non affecté'],
        isUnassigned: true,
      });
    }

    // Ensuite les autres camions triés par nom
    Object.keys(grouped)
      .filter((key) => key !== 'Non affecté')
      .sort()
      .forEach((truckName) => {
        const truck = trucks.find((t) => t.licensePlate === truckName);
        orderedGroups.push({
          truckName,
          deliveries: grouped[truckName],
          isUnassigned: false,
          truck: truck,
        });
      });

    return orderedGroups;
  };

  return (
    <GenericPageContainer
      title="Affectations Camion"
      page="TrckAffections"
      subtitle="Visualisez les commandes planifiées avec statut SCHEDULED"
      filters={
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <ReactDatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            customInput={<CustomDatePickerDashboard />}
            locale="fr"
            dateFormat="dd MMM yyyy"
          />
          {features?.TOUR_PLANNING?.TRUCK_ASSIGNMENTS?.VROOM_OPTIMIZATION
            ?.enabled && (
            <button
              onClick={handleOptimizeRoutes}
              disabled={isOptimizing || deliveries.length === 0}
              className="optimize-button"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                backgroundColor: isOptimizing ? '#cccccc' : '#079992',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor:
                  isOptimizing || deliveries.length === 0
                    ? 'not-allowed'
                    : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
              onMouseEnter={(e) => {
                if (!isOptimizing && deliveries.length > 0) {
                  e.currentTarget.style.backgroundColor = '#06867e';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow =
                    '0 4px 8px rgba(0,0,0,0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isOptimizing && deliveries.length > 0) {
                  e.currentTarget.style.backgroundColor = '#079992';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }
              }}
            >
              <FontAwesomeIcon
                icon={isOptimizing ? faSpinner : faMagic}
                spin={isOptimizing}
              />
              {isOptimizing
                ? 'Optimisation en cours...'
                : 'Optimiser les tournées'}
            </button>
          )}
          <button
            onClick={handleExportAssignments}
            disabled={deliveries.length === 0}
            className="export-button"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: deliveries.length === 0 ? '#cccccc' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: deliveries.length === 0 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
            onMouseEnter={(e) => {
              if (deliveries.length > 0) {
                e.currentTarget.style.backgroundColor = '#45a049';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (deliveries.length > 0) {
                e.currentTarget.style.backgroundColor = '#4CAF50';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }
            }}
          >
            <FontAwesomeIcon icon={faFileExport} />
            Exporter en CSV
          </button>
        </div>
      }
    >
      {/* Section KPI */}
      <div className="kpi-bar">
        <div className="kpi-container">
          <div className="kpi-item">
            <div className="kpi-content">
              <div className="kpi-value">{kpiData.totalMissions} missions</div>
              <div className="kpi-label">
                {kpiData.unassignedMissions > 0 && (
                  <span className="kpi-alert">
                    {kpiData.unassignedMissions} non affectées
                  </span>
                )}
                {kpiData.missionsWithoutETA > 0 && (
                  <span className="kpi-alert">
                    {kpiData.missionsWithoutETA} sans ETA
                  </span>
                )}
                {kpiData.unassignedMissions === 0 &&
                  kpiData.missionsWithoutETA === 0 && (
                    <span>
                      Toutes les missions sont affectées et ont un ETA
                    </span>
                  )}
              </div>
            </div>
          </div>
          <div className="kpi-item">
            <div className="kpi-content">
              <div className="kpi-value">
                {kpiData.assignedDistance.toFixed(2)} km
                {kpiData.totalTravelTime > 0 && (
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: 'normal',
                      marginLeft: '8px',
                      color: '#666',
                    }}
                  >
                    ({Math.floor(kpiData.totalTravelTime / 60)}h
                    {(kpiData.totalTravelTime % 60).toString().padStart(2, '0')}
                    )
                  </span>
                )}
              </div>
              <div className="kpi-label">Distance des tournées</div>
            </div>
          </div>
          <div className="kpi-item">
            <div className="kpi-content">
              <div className="kpi-value">
                {kpiData.assignedWeight.toFixed(2)} kg
              </div>
              <div className="kpi-label">Poids des tournées</div>
            </div>
          </div>
          <div className="kpi-item">
            <div className="kpi-content">
              <div className="kpi-value">{kpiData.totalTrucks} camions</div>
              <div className="kpi-label">
                {kpiData.assignedMissions} livraisons affectées
              </div>
            </div>
          </div>
          <div className="kpi-item">
            <div className="kpi-content">
              <div className="kpi-value">{kpiData.totalCost.toFixed(2)} €</div>
              <div className="kpi-label">Coût total</div>
            </div>
          </div>
          <div className="kpi-item">
            <div className="kpi-content">
              <div className="kpi-value">{kpiData.totalCO2.toFixed(2)} kg</div>
              <div className="kpi-label">Impact CO2</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bannière des résultats d'optimisation */}
      {showOptimizationBanner && optimizationResult && (
        <div
          className="optimization-banner"
          style={{
            backgroundColor: '#e6f7f6',
            border: '2px solid #079992',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            animation: 'slideDown 0.3s ease-out',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px',
                }}
              >
                <FontAwesomeIcon
                  icon={faMagic}
                  style={{ color: '#079992', fontSize: '20px' }}
                />
                <h3
                  style={{
                    margin: 0,
                    color: '#079992',
                    fontSize: '18px',
                    fontWeight: 'bold',
                  }}
                >
                  Optimisation VROOM effectuée avec succès
                </h3>
              </div>

              {optimizationResult.summary && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                    marginTop: '12px',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#666',
                        marginBottom: '4px',
                      }}
                    >
                      Tournées créées
                    </div>
                    <div
                      style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#079992',
                      }}
                    >
                      {optimizationResult.summary.totalRoutes}
                    </div>
                  </div>

                  {optimizationResult.summary.totalDistance && (
                    <div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#666',
                          marginBottom: '4px',
                        }}
                      >
                        Distance totale optimisée
                      </div>
                      <div
                        style={{
                          fontSize: '20px',
                          fontWeight: 'bold',
                          color: '#079992',
                        }}
                      >
                        {(
                          optimizationResult.summary.totalDistance / 1000
                        ).toFixed(2)}{' '}
                        km
                      </div>
                    </div>
                  )}

                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#666',
                        marginBottom: '4px',
                      }}
                    >
                      Durée totale
                    </div>
                    <div
                      style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#079992',
                      }}
                    >
                      {Math.floor(
                        optimizationResult.summary.totalDuration / 60
                      )}
                      h {optimizationResult.summary.totalDuration % 60}min
                    </div>
                  </div>

                  {optimizationResult.unassignedJobs &&
                    optimizationResult.unassignedJobs.length > 0 && (
                      <div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#666',
                            marginBottom: '4px',
                          }}
                        >
                          Commandes non affectées
                        </div>
                        <div
                          style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#ff9800',
                          }}
                        >
                          {optimizationResult.unassignedJobs.length}
                        </div>
                      </div>
                    )}

                  {optimizationResult.vehiclesStats && (
                    <div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#666',
                          marginBottom: '4px',
                        }}
                      >
                        Véhicules utilisés / total
                      </div>
                      <div
                        style={{
                          fontSize: '20px',
                          fontWeight: 'bold',
                          color:
                            optimizationResult.vehiclesStats.ignored > 0
                              ? '#ff9800'
                              : '#079992',
                        }}
                      >
                        {optimizationResult.vehiclesStats.used} /{' '}
                        {optimizationResult.vehiclesStats.total}
                      </div>
                    </div>
                  )}

                  {optimizationResult.impossibleJobs &&
                    optimizationResult.impossibleJobs.length > 0 && (
                      <div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#666',
                            marginBottom: '4px',
                          }}
                        >
                          Commandes impossibles
                        </div>
                        <div
                          style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#d32f2f',
                          }}
                        >
                          {optimizationResult.impossibleJobs.length}
                        </div>
                      </div>
                    )}
                </div>
              )}

              {optimizationResult.routes &&
                optimizationResult.routes.length > 0 && (
                  <div
                    style={{
                      marginTop: '16px',
                      fontSize: '13px',
                      color: '#555',
                    }}
                  >
                    <strong>Détails des tournées :</strong>
                    <ul
                      style={{
                        marginTop: '8px',
                        paddingLeft: '20px',
                        marginBottom: 0,
                      }}
                    >
                      {optimizationResult.routes
                        .slice(0, 5)
                        .map((route, index) => (
                          <li key={index} style={{ marginBottom: '4px' }}>
                            <strong>Véhicule {route.vehicleKey}</strong>:{' '}
                            {route.steps.filter((s) => s.type === 'job').length}{' '}
                            livraisons,{' '}
                            {route.totalDistance
                              ? `${(route.totalDistance / 1000).toFixed(1)} km, `
                              : ''}
                            {Math.floor(route.totalDuration / 60)}h{' '}
                            {route.totalDuration % 60}min
                          </li>
                        ))}
                      {optimizationResult.routes.length > 5 && (
                        <li style={{ color: '#666', fontStyle: 'italic' }}>
                          ... et {optimizationResult.routes.length - 5} autre(s)
                          tournée(s)
                        </li>
                      )}
                    </ul>
                  </div>
                )}

              {optimizationResult.impossibleJobs &&
                optimizationResult.impossibleJobs.length > 0 && (
                  <div
                    style={{
                      marginTop: '16px',
                      fontSize: '13px',
                      padding: '12px',
                      backgroundColor: '#ffebee',
                      border: '2px solid #d32f2f',
                      borderRadius: '6px',
                    }}
                  >
                    <strong style={{ color: '#c62828' }}>
                      ❌ Commandes impossibles à assigner :
                    </strong>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#c62828',
                        marginTop: '4px',
                        marginBottom: '8px',
                      }}
                    >
                      Ces commandes dépassent la capacité maximale de tous les
                      véhicules disponibles
                    </div>
                    <ul
                      style={{
                        marginTop: '8px',
                        paddingLeft: '20px',
                        marginBottom: 0,
                      }}
                    >
                      {optimizationResult.impossibleJobs.map((job, index) => (
                        <li
                          key={index}
                          style={{ marginBottom: '8px', color: '#c62828' }}
                        >
                          <strong>{job.clientName}</strong>
                          <div
                            style={{
                              fontSize: '11px',
                              marginTop: '2px',
                              color: '#666',
                            }}
                          >
                            {job.address}
                          </div>
                          <div
                            style={{
                              fontSize: '11px',
                              marginTop: '2px',
                              fontStyle: 'italic',
                            }}
                          >
                            {job.details}
                          </div>
                          {(job.weight || job.linearMeters) && (
                            <div
                              style={{
                                fontSize: '11px',
                                marginTop: '2px',
                                color: '#d32f2f',
                                fontWeight: 'bold',
                              }}
                            >
                              {job.weight &&
                                `Poids: ${job.weight.toFixed(2)} kg`}
                              {job.weight && job.linearMeters && ' • '}
                              {job.linearMeters &&
                                `Mètres linéaires: ${job.linearMeters.toFixed(2)} m`}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {optimizationResult.ignoredVehicles &&
                optimizationResult.ignoredVehicles.length > 0 && (
                  <div
                    style={{
                      marginTop: '16px',
                      fontSize: '13px',
                      padding: '12px',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffc107',
                      borderRadius: '6px',
                    }}
                  >
                    <strong style={{ color: '#856404' }}>
                      ⚠️ Véhicules non utilisés :
                    </strong>
                    <ul
                      style={{
                        marginTop: '8px',
                        paddingLeft: '20px',
                        marginBottom: 0,
                      }}
                    >
                      {optimizationResult.ignoredVehicles.map(
                        (vehicle, index) => (
                          <li
                            key={index}
                            style={{ marginBottom: '4px', color: '#856404' }}
                          >
                            <strong>{vehicle.licensePlate}</strong> (
                            {vehicle.vehicleKey}):{' '}
                            {vehicle.reason === 'missing_coordinates'
                              ? 'Coordonnées manquantes'
                              : vehicle.reason}
                            {vehicle.details && (
                              <div
                                style={{
                                  fontSize: '11px',
                                  marginTop: '2px',
                                  fontStyle: 'italic',
                                }}
                              >
                                {vehicle.details}
                              </div>
                            )}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}
            </div>

            <button
              onClick={() => setShowOptimizationBanner(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                color: '#079992',
                cursor: 'pointer',
                padding: '0 8px',
                marginLeft: '16px',
              }}
              title="Fermer"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Layout avec deux colonnes : Table à gauche, Carte à droite */}
      <div className="two-column-layout-assignments tw-mt-8">
        {/* Colonne de gauche : Liste des commandes groupées par camion */}
        <div className="left-column-assignments">
          {isLoadingScheduled ? (
            <GenericTableSkeleton numberOfRows={8} />
          ) : deliveries.length > 0 ? (
            <>
              {getOrderedTruckGroups()
                .filter((group) => {
                  // Si un véhicule est sélectionné, n'afficher que ce groupe (et toujours les non affectées)
                  if (!selectedVehicle) return true;
                  return (
                    group.isUnassigned || group.truckName === selectedVehicle
                  );
                })
                .map((group, index) => {
                  const totalWeight = group.deliveries.reduce(
                    (sum, delivery) => sum + delivery.weight,
                    0
                  );
                  const truckInfo = group.truck;
                  const isSelected = selectedVehicle === group.truckName;

                  // Récupérer les KPIs VROOM pour ce groupe (s'ils existent)
                  const firstDeliveryWithKpis = group.deliveries.find(
                    (d) => d.vroomKpis
                  );
                  const vroomKpis = firstDeliveryWithKpis?.vroomKpis;

                  // Formater le temps de trajet
                  let travelTimeText = '';
                  if (
                    vroomKpis &&
                    vroomKpis.travelTime !== null &&
                    vroomKpis.travelTime > 0
                  ) {
                    const hours = Math.floor(vroomKpis.travelTime / 60);
                    const minutes = vroomKpis.travelTime % 60;
                    travelTimeText =
                      hours > 0
                        ? ` - Trajet: ${hours}h${minutes.toString().padStart(2, '0')}`
                        : ` - Trajet: ${minutes}min`;
                  }

                  // Formater la distance de la tournée
                  let distanceText = '';
                  if (
                    vroomKpis &&
                    vroomKpis.totalDistance !== null &&
                    vroomKpis.totalDistance > 0
                  ) {
                    const distanceKm = (vroomKpis.totalDistance / 1000).toFixed(
                      2
                    );
                    distanceText = ` - Distance: ${distanceKm} km`;
                  }

                  return (
                    <div
                      key={group.truckName}
                      className={index > 0 ? 'tw-mt-6' : ''}
                    >
                      <GenericTable
                        title={
                          group.isUnassigned ? (
                            `Commandes non affectées (${group.deliveries.length})`
                          ) : (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                flexWrap: 'wrap',
                              }}
                            >
                              <span
                                onClick={() =>
                                  handleVehicleClick(group.truckName)
                                }
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '4px 12px',
                                  borderRadius: '6px',
                                  backgroundColor: isSelected
                                    ? truckInfo?.color || '#079992'
                                    : lightenColor(
                                        truckInfo?.color || '#079992',
                                        85
                                      ),
                                  color: isSelected
                                    ? 'white'
                                    : truckInfo?.color || '#079992',
                                  fontWeight: '600',
                                  fontSize: '14px',
                                  border: isSelected
                                    ? `2px solid ${truckInfo?.color || '#079992'}`
                                    : `1px solid ${truckInfo?.color || '#079992'}20`,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  boxShadow: isSelected
                                    ? '0 2px 8px rgba(0,0,0,0.2)'
                                    : 'none',
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor =
                                      lightenColor(
                                        truckInfo?.color || '#079992',
                                        70
                                      );
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor =
                                      lightenColor(
                                        truckInfo?.color || '#079992',
                                        85
                                      );
                                  }
                                }}
                                title={
                                  isSelected
                                    ? 'Cliquer pour désélectionner'
                                    : 'Cliquer pour filtrer ce véhicule'
                                }
                              >
                                Camion {group.truckName}
                                {isSelected && ' ✓'}
                              </span>
                              <span style={{ color: '#666', fontSize: '14px' }}>
                                {group.deliveries.length} commandes -{' '}
                                {totalWeight.toFixed(2)} kg
                                {truckInfo
                                  ? ` / ${truckInfo.capacity} kg - ${((totalWeight / truckInfo.capacity) * 100).toFixed(0)}%`
                                  : ''}
                                {travelTimeText}
                                {distanceText}
                              </span>
                            </div>
                          )
                        }
                        rows={prepareTableRowsForTruck(
                          group.deliveries,
                          group.isUnassigned
                        )}
                        icon={faBoxesPacking}
                        iconColor={
                          group.isUnassigned
                            ? 'tw-text-gray-400'
                            : 'tw-text-primary'
                        }
                        headerAction={
                          group.deliveries.length > 0 ? (
                            group.isUnassigned ? (
                              // Bouton pour les commandes non affectées
                              <button
                                onClick={() => setShowRescheduleModal(true)}
                                disabled={isRescheduling}
                                className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-border-none tw-rounded-md disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
                                style={{
                                  backgroundColor: isRescheduling
                                    ? '#cccccc'
                                    : '#079992',
                                  color: 'white',
                                }}
                                onMouseEnter={(e) => {
                                  if (!isRescheduling) {
                                    e.currentTarget.style.backgroundColor =
                                      '#079992';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isRescheduling) {
                                    e.currentTarget.style.backgroundColor =
                                      '#079992';
                                  }
                                }}
                                title="Déplacer toutes les commandes non affectées vers une autre date"
                              >
                                <FontAwesomeIcon icon={faCalendar} />
                                <span>Replanifier</span>
                              </button>
                            ) : (
                              // Boutons pour les camions affectés
                              <div
                                style={{
                                  display: 'flex',
                                  gap: '8px',
                                  alignItems: 'center',
                                }}
                              >
                                <button
                                  onClick={() =>
                                    handleOptimizeVehicle(truckInfo)
                                  }
                                  disabled={isOptimizingVehicle || !truckInfo}
                                  className="tw-flex tw-items-center tw-justify-center tw-p-2 tw-text-sm tw-font-medium tw-bg-primary tw-border-none tw-rounded-md disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
                                  style={{
                                    backgroundColor: isOptimizingVehicle
                                      ? '#cccccc'
                                      : '#079992',
                                    color: 'white',
                                    width: '36px',
                                    height: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isOptimizingVehicle && truckInfo) {
                                      e.currentTarget.style.backgroundColor =
                                        '#06867e';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isOptimizingVehicle && truckInfo) {
                                      e.currentTarget.style.backgroundColor =
                                        '#079992';
                                    }
                                  }}
                                  title={`Optimiser l'ordre des livraisons pour ce véhicule`}
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      isOptimizingVehicle ? faSpinner : faMagic
                                    }
                                    spin={isOptimizingVehicle}
                                  />
                                </button>
                                <button
                                  onClick={() =>
                                    handleUnassignAllFromTruck(
                                      group.truckName,
                                      group.deliveries
                                    )
                                  }
                                  disabled={isUnassigning}
                                  className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-red-600 tw-bg-red-50 tw-border-none tw-rounded-md hover:tw-bg-red-100 hover:tw-border-red-300 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
                                  title="Désaffecter toutes les commandes de ce camion"
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                  <span>Vider</span>
                                </button>
                              </div>
                            )
                          ) : null
                        }
                      />
                    </div>
                  );
                })}
            </>
          ) : (
            <EmptyList />
          )}
        </div>

        {/* Colonne de droite : Carte */}
        <div className="right-column-assignments">
          <div className="map-section-column">
            <div className="map-container-assignments">
              {factoryCoordinates && factoryCoordinates.length > 0 && (
                <MapContainer
                  ref={mapRef}
                  center={[
                    factoryCoordinates[0].lat,
                    factoryCoordinates[0].lng,
                  ]}
                  zoom={6}
                  style={{ height: '100%', width: '100%', borderRadius: '8px' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  />

                  {/* Afficher le nom du véhicule sélectionné sur la carte */}
                  {selectedVehicle && (
                    <VehicleNameOverlay
                      vehicleName={selectedVehicle}
                      vehicleColor={
                        trucks.find((t) => t.licensePlate === selectedVehicle)
                          ?.color || '#079992'
                      }
                      onClose={() => setSelectedVehicle(null)}
                    />
                  )}

                  {/* Marqueurs de toutes les usines */}
                  {factoryCoordinates.map((factory, index) => (
                    <CircleMarker
                      key={factory.id || `factory-${index}`}
                      center={[factory.lat, factory.lng]}
                      radius={12}
                      fillColor="#079992"
                      color="white"
                      weight={3}
                      fillOpacity={1}
                    >
                      <Popup>
                        <div>
                          <strong>{factory.name}</strong>
                        </div>
                      </Popup>
                    </CircleMarker>
                  ))}

                  {/* Itinéraires optimisés des camions */}
                  {optimizedRoutes
                    .filter((route) => {
                      // Filtrer les routes selon le véhicule sélectionné
                      if (!selectedVehicle) return true;
                      // Trouver le camion correspondant à cette route
                      const truck = trucks.find(
                        (t) =>
                          t.id === route.vehicleId || t.key === route.vehicleKey
                      );
                      return truck && truck.licensePlate === selectedVehicle;
                    })
                    .map((route) => (
                      <Polyline
                        key={`route-${route.vehicleKey}`}
                        positions={route.coordinates}
                        pathOptions={{
                          color: route.color,
                          weight: 4,
                          opacity: 0.7,
                          lineJoin: 'round',
                          lineCap: 'round',
                        }}
                      >
                        <Popup>
                          <div>
                            <strong>Itinéraire {route.vehicleKey}</strong>
                            <br />
                            {route.totalDistance && (
                              <>
                                Distance:{' '}
                                {(route.totalDistance / 1000).toFixed(2)} km
                                <br />
                              </>
                            )}
                            {route.totalDuration && (
                              <>
                                Durée: {Math.floor(route.totalDuration / 60)}h{' '}
                                {route.totalDuration % 60}min
                              </>
                            )}
                          </div>
                        </Popup>
                      </Polyline>
                    ))}

                  {/* Marqueurs des livraisons avec clusters */}
                  <MarkerClusterGroup
                    chunkedLoading
                    iconCreateFunction={createClusterCustomIcon}
                    maxClusterRadius={50}
                  >
                    {deliveries
                      .filter((delivery) => {
                        // Filtrer les marqueurs selon le véhicule sélectionné
                        if (!selectedVehicle) return true;
                        // Toujours afficher les commandes non affectées
                        if (
                          delivery.truck === 'Non affecté' ||
                          !delivery.vehicleId
                        )
                          return true;
                        return delivery.truck === selectedVehicle;
                      })
                      .map((delivery) => {
                        if (!delivery.coordinates) return null;

                        const color = truckColors[delivery.id] || '#cccccc';
                        const isUnassigned =
                          delivery.truck === 'Non affecté' ||
                          !delivery.vehicleId;
                        const displayText = isUnassigned
                          ? '?'
                          : delivery.order || '';

                        const customIcon = L.divIcon({
                          html: `
                          <div style="
                            background-color: ${color};
                            width: 28px;
                            height: 28px;
                            border-radius: 50%;
                            border: 3px solid white;
                            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-weight: bold;
                            font-size: ${isUnassigned ? '16px' : '12px'};
                            color: white;
                            text-shadow: 0 1px 2px rgba(0,0,0,0.5);
                          ">${displayText}</div>
                        `,
                          className: 'custom-delivery-marker',
                          iconSize: [28, 28],
                          iconAnchor: [14, 14],
                        });

                        return (
                          <Marker
                            key={delivery.id}
                            position={[
                              delivery.coordinates.lat,
                              delivery.coordinates.lng,
                            ]}
                            icon={customIcon}
                          >
                            <Popup>
                              <div>
                                <strong>{delivery.client}</strong>
                                <br />
                                {delivery.address}
                                <br />
                                <em>Camion: {delivery.truck}</em>
                                <br />
                                <em>Poids: {delivery.weight} kg</em>
                                {delivery.etaTime && (
                                  <>
                                    <br />
                                    <em>ETA: {delivery.etaTime}</em>
                                  </>
                                )}
                                {isUnassigned && (
                                  <>
                                    <br />
                                    <button
                                      onClick={() =>
                                        handleOpenAssignmentModal(delivery)
                                      }
                                      disabled={isAssigning}
                                      style={{
                                        marginTop: '8px',
                                        padding: '6px 12px',
                                        backgroundColor: '#079992',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        cursor: isAssigning
                                          ? 'not-allowed'
                                          : 'pointer',
                                        width: '100%',
                                        opacity: isAssigning ? 0.6 : 1,
                                        transition: 'all 0.2s ease',
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isAssigning) {
                                          e.currentTarget.style.backgroundColor =
                                            '#06867e';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isAssigning) {
                                          e.currentTarget.style.backgroundColor =
                                            '#079992';
                                        }
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={faPlus}
                                        style={{ marginRight: '6px' }}
                                      />
                                      Affecter à un camion
                                    </button>
                                  </>
                                )}
                              </div>
                            </Popup>
                          </Marker>
                        );
                      })}
                  </MarkerClusterGroup>
                </MapContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modale d'affectation manuelle */}
      <ManualAssignmentModal
        isOpen={showAssignmentModal}
        onClose={handleCloseAssignmentModal}
        delivery={selectedDeliveryForAssignment}
        trucks={trucks}
        onAssign={handleManualAssignment}
        isAssigning={isAssigning}
        autoOptimize={autoOptimizeAfterAssignment}
        onAutoOptimizeChange={setAutoOptimizeAfterAssignment}
      />

      {/* Modale de replanification */}
      <RescheduleDateModal
        isOpen={showRescheduleModal}
        onClose={() => setShowRescheduleModal(false)}
        unassignedCount={
          deliveries.filter(
            (d) => !d.vehicleId && (d.truck === 'Non affecté' || !d.truck)
          ).length
        }
        onReschedule={handleRescheduleUnassigned}
        isRescheduling={isRescheduling}
      />
    </GenericPageContainer>
  );
}

